// Sprint-009 B4 — pure date arithmetic for the calendar's month/week grids
// and phone day strip (design-phase-b.md §2.3/§2.4). Every date here is a
// plain "YYYY-MM-DD" string or a `Date` constructed from midnight-LOCAL via
// `T00:00:00` (never UTC-shifted) — the same off-by-one-day guard
// `lib/recordsCopy.ts::monthDayLabel` already uses. No date-fns/day.js
// dependency: this app has no calendar-math library on the tree yet and
// every operation needed here (start-of-week, add-days, grid-build) is a
// handful of lines of native `Date` arithmetic — not worth a new
// dependency for (matches this codebase's existing "small utility,
// hand-rolled" posture, e.g. `lib/personColor.ts`'s own hash function).

const WEEKDAY_SHORT_FORMATTER = new Intl.DateTimeFormat(undefined, { weekday: "short" });
const WEEKDAY_FULL_FORMATTER = new Intl.DateTimeFormat(undefined, { weekday: "long" });
const WEEKDAY_NARROW_FORMATTER = new Intl.DateTimeFormat(undefined, { weekday: "narrow" });
const MONTH_LABEL_FORMATTER = new Intl.DateTimeFormat(undefined, {
  month: "long",
  year: "numeric",
});
const WEEK_RANGE_START_FORMATTER = new Intl.DateTimeFormat(undefined, {
  month: "short",
  day: "numeric",
});

export function parseISODate(iso: string): Date {
  return new Date(`${iso}T00:00:00`);
}

export function toISODate(date: Date): string {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const d = String(date.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
}

export function todayLocal(): Date {
  const now = new Date();
  return new Date(now.getFullYear(), now.getMonth(), now.getDate());
}

export function addDays(date: Date, days: number): Date {
  const next = new Date(date);
  next.setDate(next.getDate() + days);
  return next;
}

/** `setDate(1)` first avoids the classic month-length-overflow bug (e.g.
 * Jan 31 + 1 month landing on Mar 3 instead of Feb 28/29). */
export function addMonths(date: Date, months: number): Date {
  const next = new Date(date);
  next.setDate(1);
  next.setMonth(next.getMonth() + months);
  return next;
}

export function startOfMonth(date: Date): Date {
  return new Date(date.getFullYear(), date.getMonth(), 1);
}

export function endOfMonth(date: Date): Date {
  return new Date(date.getFullYear(), date.getMonth() + 1, 0);
}

/** Sunday-start week (design-phase-b.md §2.3: "farm-local week start —
 * reuse whatever day-of-week convention `AddHealthNoteSheet`'s date
 * handling already assumes." That component makes no weekday assumption of
 * its own, so this is the one new convention this spec introduces —
 * Sunday-first, the US-default `Intl`/`<input type=date>` convention this
 * app already relies on elsewhere and the ordering the mock's own
 * `Sun…Sat` header row names literally, §2.3.) */
export function startOfWeek(date: Date): Date {
  return addDays(date, -date.getDay());
}

export function isSameDay(a: Date, b: Date): boolean {
  return toISODate(a) === toISODate(b);
}

/** Every date cell for the month `<table>` (§2.3) — always full weeks
 * (Sun-Sat), including leading/trailing outside-month days so every row
 * has exactly 7 cells. */
export function buildMonthGrid(monthDate: Date): Date[][] {
  const gridStart = startOfWeek(startOfMonth(monthDate));
  const gridEndAnchor = addDays(startOfWeek(endOfMonth(monthDate)), 6);
  const weeks: Date[][] = [];
  let cursor = gridStart;
  while (cursor.getTime() <= gridEndAnchor.getTime()) {
    const week: Date[] = [];
    for (let i = 0; i < 7; i += 1) {
      week.push(cursor);
      cursor = addDays(cursor, 1);
    }
    weeks.push(week);
  }
  return weeks;
}

/** The 7 dates (Sun-Sat) of the week containing `date` — the phone day
 * strip (§2.4) and the desktop week view's 7 headed columns (§2.12). */
export function buildWeekDays(date: Date): Date[] {
  const start = startOfWeek(date);
  return Array.from({ length: 7 }, (_, i) => addDays(start, i));
}

export function weekdayShortLabel(date: Date): string {
  return WEEKDAY_SHORT_FORMATTER.format(date);
}

export function weekdayFullLabel(date: Date): string {
  return WEEKDAY_FULL_FORMATTER.format(date);
}

export function weekdayNarrowLabel(date: Date): string {
  return WEEKDAY_NARROW_FORMATTER.format(date);
}

export function monthLabel(date: Date): string {
  return MONTH_LABEL_FORMATTER.format(date);
}

/** "Jul 6–12" — the week toolbar's own subheading (§2.2). */
export function weekRangeLabel(weekDays: Date[]): string {
  const start = weekDays[0] as Date;
  const end = weekDays[weekDays.length - 1] as Date;
  return `${WEEK_RANGE_START_FORMATTER.format(start)}–${end.getDate()}`;
}

/** ADR-002 Amendment 5 decision 1.2's flexible-array key: one entry per
 * ISO week (Monday-start), `week_start`/`week_end` server-rendered. The
 * client only ever needs to find "the current ISO week's own `week_start`"
 * to filter the "This week" band (§2.6) — this is that Monday, independent
 * of this file's own Sunday-start GRID convention (the two conventions
 * intentionally don't have to agree; the grid is a UI layout choice, the
 * ISO week is the server's own flexible-cadence windowing unit). */
export function isoWeekStart(date: Date): Date {
  const day = date.getDay();
  const diff = day === 0 ? -6 : 1 - day;
  return addDays(date, diff);
}
